/**
 * Zone Content Generation Utilities
 *
 * Provides functions to generate formatted content for map popups and detail views
 * based on zone features and datasource field configurations.
 */

import { AttributeSort } from "@ciq/shared/utils/sort"
import { formatString } from "@ciq/shared/utils/format"
import { isEmptyObject } from "@ciq/shared/utils/object"
import { isEmptyArray } from "@ciq/shared/utils/array"

export const EMPTY_ZONE_DATA_TAG = "<div>No zone data available</div>"

/**
 * Strip extra whitespace from HTML string
 * @param {string} html - HTML string to clean
 * @returns {string} - HTML string with extra whitespace removed
 */
export const stripHtmlWhitespace = html => {
  return html
    .trim() // Step 1: trim left and right
    .replace(/>[ \t]+</g, "><") // Step 2: replace spaces/tabs between '>' and '<' while preserving newlines
    .replace(/>[ \t]*\n[ \t]*</g, "><") // Handle newlines between tags too
    .replace(/>[ \t]*\n[ \t]*([^<\n]+)/g, "> $1") // Remove whitespace after > but add space before text
    .replace(/([^>\n]+)[ \t]*\n[ \t]*</g, "$1 <") // Remove whitespace before < but add space after text
    .replace(/> ([^<]+) </g, ">$1<") // Remove spaces immediately after > and before < for single text content
}

/**
 * Generate popup HTML content for a zone feature
 * @param {Object} feature - GeoJSON feature with zone properties
 * @param {Array<{
 *   group: string,
 *   name: string,
 *   description: string|null,
 *   value: string
 * }>} mappings - Array of metric mapping configurations
 * @returns {string} - HTML content for the popup
 */
export const generateZonePopupContent = (zone, columns, attributes) => {
  if (isEmptyObject(zone)) {
    return EMPTY_ZONE_DATA_TAG
  }

  const { name, description } = zone

  if (isEmptyArray(columns)) {
    return MapPopup({ name, description })
  }

  // build data by group
  const groupedData = {}

  columns.forEach(column => {
    const rawValue = attributes?.[column.name]

    // Skip null/undefined values - consistent with right pane behavior
    if (rawValue == null) {
      return
    }

    groupedData[column.group] ??= []
    groupedData[column.group].push({
      name: column.label,
      value: formatString(column.formatValue(rawValue)),
    })
  })

  const groupData = []
  for (const groupName of Object.keys(groupedData).sort()) {
    groupData.push({
      name: groupName,
      metrics: groupedData[groupName].sort(AttributeSort("name")),
    })
  }

  return MapPopup({ name, description, groups: groupData })
}

/**
 * Generate HTML string for map popup content
 * @param {Object} params - Popup parameters
 * @param {string} params.name - Zone name
 * @param {string} params.description - Zone description
 * @param {Array<{name: string, metrics: Array<{name: string, value: string}>}>} params.groups - Array of grouped metrics data
 * @returns {string} - HTML string for the popup
 */
function MapPopup({ name, description, groups }) {
  let html = `
  <div class="map-popup">
    <div class="map-popup__header">
      <span>${name || "Unknown"}</span>
      <span>${description || ""}</span>
    </div>`

  if (!groups?.length) {
    html += "</div>"
    return stripHtmlWhitespace(html)
  }

  for (const group of groups) {
    if (!group.metrics.length) {
      continue
    }

    html += `
    <div class="map-popup__metrics">
      <span>${group.name}</span>`

    for (const metric of group.metrics) {
      html += `
      <div class="map-popup__metrics__metric">
        <span>${metric.name}:</span>
        <span>${formatString(metric.value)}</span>
      </div>`
    }
    html += "</div>"
  }

  html += "</div>"
  return stripHtmlWhitespace(html)
}
